/**
 * @fileoverview Form state and save flow for the customer company user modal.
 * @module hooks/useCustomerCompanyUserModal
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { logger, sanitizeError } from "../utils/logger.js";
import { getCountryCodeOptions } from "../data/selectOptions";
import { validateEmail } from "../utils/common.js";

const HTTP_INTERNAL_SERVER_ERROR = 500;
const HTTP_CONFLICT = 409;
const EXISTING_USER_ACCOUNT_MESSAGE = "User account already exists";
const EMAIL_ALREADY_EXISTS = "EMAIL_ALREADY_EXISTS";

const isExistingUserAccountConflict = (status, data) =>
  (status === HTTP_INTERNAL_SERVER_ERROR &&
    Boolean(data.message) &&
    data.message.includes(EXISTING_USER_ACCOUNT_MESSAGE)) ||
  (status === HTTP_CONFLICT && data.errorType === EMAIL_ALREADY_EXISTS);

const useCustomerCompanyUserModal = ({
  open,
  onClose,
  companyId,
  user,
  mode = "create",
  onUserCreated,
}) => {
  const { t } = useTranslation();
  const countryCodeOptions = getCountryCodeOptions(t);
  const dialogRef = useRef(null);
  const [name, setName] = useState(user?.nombre || "");
  const [email, setEmail] = useState(user?.correoElectronico || "");
  const [phoneCountryCode, setPhoneCountryCode] = useState(
    user?.telefonoCodigoPais || null,
  );
  const [phone, setPhone] = useState(user?.telefono || "");
  const [position, setPosition] = useState(user?.cargo || "");
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(user?.compradorActivo || false);
  const [emailError, setEmailError] = useState(false);
  const [existingUser, setExistingUser] = useState(null);
  const isEditMode = mode === "edit";

  useEffect(() => {
    if (user && isEditMode) {
      setName(user.nombre || "");
      setEmail(user.correoElectronico || "");
      setPhoneCountryCode(user.telefonoCodigoPais || null);
      setPhone(user.telefono || "");
      setPosition(user.cargo || "");
      setEnabled(user.compradorActivo || false);
    } else if (!isEditMode) {
      setName("");
      setEmail("");
      setPhoneCountryCode("");
      setPhone("");
      setPosition("");
      setEnabled(true);
      setEmailError(false);
      setExistingUser(null);
    } else {
      // Edit mode without user data — keep current form values
    }
  }, [user, isEditMode]);

  useEffect(() => {
    if (open && !isEditMode) {
      setName("");
      setEmail("");
      setPhoneCountryCode("");
      setPhone("");
      setPosition("");
      setEnabled(true);
      setEmailError(false);
      setExistingUser(null);
    }
  }, [open, isEditMode]);

  const emailFormatInvalid =
    !isEditMode && email.trim() !== "" && !validateEmail(email).isValid;

  const isFormValid = () =>
    name.trim() !== "" &&
    email.trim() !== "" &&
    (isEditMode || validateEmail(email).isValid) &&
    position.trim() !== "" &&
    phoneCountryCode !== "" &&
    phone.trim() !== "";

  useEffect(() => {
    const handleDialogClose = () => {
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleDialogClose);
    }

    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleDialogClose);
      }
    };
  }, [open, onClose]);

  const handleInputChange = useCallback((setter, value) => {
    setter(value);
    if (setter === setEmail) {
      setEmailError(false);
      setExistingUser(null);
    }
  }, []);

  const handleNameChange = useCallback(
    (event) => {
      handleInputChange(setName, event.target.value);
    },
    [handleInputChange],
  );

  const handleEmailChange = useCallback(
    (event) => {
      handleInputChange(setEmail, event.target.value);
    },
    [handleInputChange],
  );

  const handlePositionChange = useCallback(
    (event) => {
      handleInputChange(setPosition, event.target.value);
    },
    [handleInputChange],
  );

  const handleCountryCodeChange = useCallback(
    (value) => {
      handleInputChange(setPhoneCountryCode, value.value);
    },
    [handleInputChange],
  );

  const handlePhoneChange = useCallback(
    (event) => {
      handleInputChange(setPhone, event.target.value);
    },
    [handleInputChange],
  );

  const handleEnabledChange = useCallback(
    (event) => {
      handleInputChange(setEnabled, event.target.checked);
    },
    [handleInputChange],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isFormValid()) {
      return;
    }
    setLoading(true);

    try {
      const url = isEditMode
        ? `${process.env.REACT_APP_ROOT_API}customerCompanyUser/${user._id}`
        : `${process.env.REACT_APP_ROOT_API}customerCompanyUser`;

      const response = await fetch(url, {
        method: isEditMode ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          nombre: name,
          correoElectronico: email,
          cargo: position,
          telefonoCodigoPais: phoneCountryCode,
          telefono: phone,
          idEmpresa: companyId,
          activo: enabled,
        }),
      });

      if (response.ok) {
        onClose();
        if (onUserCreated) {
          onUserCreated();
        }
      } else {
        const data = await response.json();
        if (isExistingUserAccountConflict(response.status, data)) {
          setEmailError(true);
          setExistingUser(data.existingUser || null);
        }
        logger.error(data);
      }
    } catch (submitError) {
      logger.error("Error", { err: sanitizeError(submitError) });
    } finally {
      setLoading(false);
    }
  };

  const getEmailErrorMessage = () => {
    if (emailFormatInvalid) {
      return t("errors.validation.INVALID_EMAIL");
    }
    if (!existingUser?.nombre) {
      return t("components.customerCompanyUserModal.emailError");
    }

    if (existingUser.idEmpresa === companyId) {
      return t("components.customerCompanyUserModal.emailExistsHere", {
        name: existingUser.nombre,
      });
    }

    if (existingUser.empresa) {
      return t("components.customerCompanyUserModal.emailExistsElsewhere", {
        name: existingUser.nombre,
        company: existingUser.empresa,
      });
    }

    return t(
      "components.customerCompanyUserModal.emailExistsElsewhereNoCompany",
      {
        name: existingUser.nombre,
      },
    );
  };

  const getModalTitle = () =>
    isEditMode
      ? t("components.customerCompanyUserModal.editTitle")
      : t("components.customerCompanyUserModal.createTitle");

  const getSubmitButtonText = () => {
    if (loading) {
      return isEditMode
        ? t("components.customerCompanyUserModal.saving")
        : t("components.customerCompanyUserModal.creating");
    }
    return isEditMode
      ? t("components.customerCompanyUserModal.saveButton")
      : t("components.customerCompanyUserModal.createButton");
  };

  return {
    t,
    countryCodeOptions,
    dialogRef,
    name,
    email,
    phoneCountryCode,
    phone,
    position,
    loading,
    enabled,
    emailError,
    emailFormatInvalid,
    isEditMode,
    isFormValid,
    handleSubmit,
    handleNameChange,
    handleEmailChange,
    handlePositionChange,
    handleCountryCodeChange,
    handlePhoneChange,
    handleEnabledChange,
    getEmailErrorMessage,
    getModalTitle,
    getSubmitButtonText,
  };
};

export default useCustomerCompanyUserModal;
